import { browserAgent } from "@/shared/telemetry/newrelic";

const IDLE_TIMEOUT_MS = 3 * 60 * 1000;
const BACKGROUND_TIMEOUT_MS = 5 * 60 * 1000;

let lastActivityTime = Date.now();
let backgroundStartTime: number | null = null;
let idleTimeoutId: ReturnType<typeof setTimeout> | null = null;
let backgroundTimeoutId: ReturnType<typeof setTimeout> | null = null;
let hasLoggedDropoff = false;
let currentCheckoutId: string | null = null;
let currentOrderId: string | null = null;

function getScreenNameFromPath(pathname: string): string {
  if (pathname === "/" || pathname === "") return "payment_plan_selection";
  if (
    pathname.includes("/confirmation") ||
    pathname.includes("/3ds") ||
    pathname.includes("/instalment")
  ) {
    return "payment_confirmation";
  }
  if (pathname.includes("/ivr")) return "ivr_verification";
  if (pathname.includes("/success")) return "success";
  if (pathname.includes("/not-found")) return "not_found";
  return "unknown";
}

function logDropoff(
  dropoffType: string,
  additionalData: Record<string, unknown> = {},
): void {
  const currentPath = window.location.pathname;
  const screenName = getScreenNameFromPath(currentPath);

  if (screenName === "success" || screenName === "not_found") return;
  if (hasLoggedDropoff) return;
  hasLoggedDropoff = true;

  browserAgent.log("CHECKOUT_DROPOFF", {
    customAttributes: {
      dropoff_type: dropoffType,
      screen_name: screenName,
      pathname: currentPath,
      timestamp: Date.now(),
      time_since_last_activity_ms: Date.now() - lastActivityTime,
      checkout_id: currentCheckoutId,
      order_id: currentOrderId,
      ...additionalData,
    },
  });
}

function resetIdleTimer(): void {
  lastActivityTime = Date.now();

  if (idleTimeoutId) clearTimeout(idleTimeoutId);

  idleTimeoutId = setTimeout(() => {
    logDropoff("idle_timeout", { idle_duration_ms: IDLE_TIMEOUT_MS });
  }, IDLE_TIMEOUT_MS);
}

function handleVisibilityChange(): void {
  const currentPath = window.location.pathname;
  const screenName = getScreenNameFromPath(currentPath);

  if (document.visibilityState === "hidden") {
    backgroundStartTime = Date.now();

    browserAgent.log("TAB_BACKGROUNDED", {
      customAttributes: {
        screen_name: screenName,
        pathname: currentPath,
        timestamp: backgroundStartTime,
        checkout_id: currentCheckoutId,
        order_id: currentOrderId,
      },
    });

    backgroundTimeoutId = setTimeout(() => {
      logDropoff("background_timeout", {
        background_duration_ms: BACKGROUND_TIMEOUT_MS,
      });
    }, BACKGROUND_TIMEOUT_MS);
  } else if (document.visibilityState === "visible") {
    const backgroundDuration = backgroundStartTime
      ? Date.now() - backgroundStartTime
      : 0;

    if (backgroundTimeoutId) {
      clearTimeout(backgroundTimeoutId);
      backgroundTimeoutId = null;
    }

    browserAgent.log("TAB_FOREGROUNDED", {
      customAttributes: {
        screen_name: screenName,
        pathname: currentPath,
        background_duration_ms: backgroundDuration,
        timestamp: Date.now(),
        checkout_id: currentCheckoutId,
        order_id: currentOrderId,
      },
    });

    backgroundStartTime = null;
    hasLoggedDropoff = false;
    resetIdleTimer();
  }
}

function handleBeforeUnload(): void {
  logDropoff("browser_close");
}

function handleUserActivity(): void {
  resetIdleTimer();
}

function updateDropoffContext(checkoutId: string, orderId: string): void {
  currentCheckoutId = checkoutId;
  currentOrderId = orderId;
}

function resetDropoffState(): void {
  hasLoggedDropoff = false;
  resetIdleTimer();
}

function setupDropoffTracking(): () => void {
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("beforeunload", handleBeforeUnload);

  const activityEvents = [
    "mousedown",
    "mousemove",
    "keydown",
    "scroll",
    "touchstart",
    "click",
  ];
  activityEvents.forEach((event) => {
    document.addEventListener(event, handleUserActivity, { passive: true });
  });

  resetIdleTimer();

  return () => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("beforeunload", handleBeforeUnload);
    activityEvents.forEach((event) => {
      document.removeEventListener(event, handleUserActivity);
    });
    if (idleTimeoutId) clearTimeout(idleTimeoutId);
    if (backgroundTimeoutId) clearTimeout(backgroundTimeoutId);
  };
}

export { updateDropoffContext, resetDropoffState, setupDropoffTracking };
